import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './input';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { MapPin, Loader2 } from 'lucide-react';
import {
  getGeocodingSuggestions,
  getGeocodingSuggestionsCached,
  getGeocodingSuggestionsClosestCached,
} from '@/lib/mapbox';

interface PlaceResult {
  id: string;
  place_name: string;
  place_name_en: string;
  text: string;
  center: [number, number]; // [longitude, latitude]
  context?: Array<{
    id: string;
    text: string;
    short_code?: string;
  }>;
}

export interface PlaceData {
  name: string;
  address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude: number;
  longitude: number;
}

export function parsePlaceResult(place: { center: [number, number]; text: string; place_name: string; context?: Array<{ id: string; text: string; short_code?: string }> }): PlaceData {
  const [longitude, latitude] = place.center;
  let parentCity = '';
  let locality = '';
  let state = '';
  let country = 'US';
  let postal_code = '';

  if (place.context) {
    place.context.forEach(ctx => {
      if (ctx.id.startsWith('locality')) {
        locality = ctx.text;
      } else if (ctx.id.startsWith('neighborhood') && !locality) {
        locality = ctx.text;
      } else if (ctx.id.startsWith('place')) {
        parentCity = ctx.text;
      } else if (ctx.id.startsWith('region')) {
        const raw = ctx.short_code || ctx.text || '';
        state = raw.replace(/^US-/i, '');
      } else if (ctx.id.startsWith('country')) {
        country = ctx.short_code || ctx.text;
      } else if (ctx.id.startsWith('postcode')) {
        postal_code = ctx.text;
      }
    });
  }

  const city = locality || place.text || parentCity;

  return {
    name: place.place_name,
    address: place.place_name,
    city,
    state: state.replace('US-', ''),
    country,
    postal_code,
    latitude,
    longitude
  };
}

interface PlaceAutocompleteProps {
  value: string;
  onChange: (value: string, placeData?: PlaceData) => void;
  placeholder?: string;
  className?: string;
  error?: string;
}


export const PlaceAutocomplete: React.FC<PlaceAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter location",
  className,
  error,
}) => {
  const [suggestions, setSuggestions] = useState<PlaceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const lastQueryRef = useRef<string>('');

  const getInstantResults = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return [] as PlaceResult[];

    const data =
      getGeocodingSuggestionsCached(trimmed) ??
      getGeocodingSuggestionsClosestCached(trimmed);

    const results = (data?.features ?? []) as PlaceResult[];
    return results;
  }, []);

  const applyInstantResults = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      if (!trimmed) {
        setSuggestions([]);
        setShowSuggestions(false);
        setSelectedIndex(-1);
        return false;
      }

      const results = getInstantResults(trimmed);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSelectedIndex(-1);
      return results.length > 0;
    },
    [getInstantResults]
  );

  const fetchSuggestions = useCallback(async (query: string) => {
    const trimmed = query.trim();

    if (!trimmed || trimmed.length < 2) {
      return;
    }

    // If the exact result is already cached, we can skip the network request.
    if (getGeocodingSuggestionsCached(trimmed)) {
      return;
    }

    lastQueryRef.current = query;

    try {
      const data = await getGeocodingSuggestions(trimmed);
      const results = (data.features || []) as PlaceResult[];

      // Only update if this is still the latest query
      if (lastQueryRef.current === query) {
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
        setSelectedIndex(-1);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      if (lastQueryRef.current === query) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } finally {
      if (lastQueryRef.current === query) {
        setIsLoading(false);
      }
    }
  }, []);

  const debouncedFetch = useCallback(
    (query: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Increased debounce to 350ms (industry standard) to reduce API costs
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, 350);
    },
    [fetchSuggestions]
  );


  // Position dropdown using a portal to avoid clipping/stacking context issues
  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4, // mt-1
      left: rect.left,
      width: rect.width,
      zIndex: 10003, // Higher than dialog overlay (10002)
    });
  }, []);

  useEffect(() => {
    if (showSuggestions) {
      updateMenuPosition();
    }
  }, [showSuggestions, updateMenuPosition, value, suggestions.length]);

  useEffect(() => {
    if (!showSuggestions) return;
    const handler = () => updateMenuPosition();
    window.addEventListener('resize', handler);
    document.addEventListener('scroll', handler, true); // capture scrolling containers
    return () => {
      window.removeEventListener('resize', handler);
      document.removeEventListener('scroll', handler, true);
    };
  }, [showSuggestions, updateMenuPosition]);

  const parsePlace = (place: PlaceResult): PlaceData => parsePlaceResult(place);

  const handleSelectPlace = (place: PlaceResult) => {
    const placeData = parsePlace(place);
    const canonicalLabel = placeData.city && placeData.state ? `${placeData.city}, ${placeData.state}` : place.place_name;
    onChange(canonicalLabel, placeData);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Instant UI update from cache / closest cached prefix
    const hasInstant = applyInstantResults(newValue);

    // Only show a spinner when we have nothing to show yet (never for cached results)
    const shouldFetch = newValue.trim().length >= 2;
    setIsLoading(shouldFetch && !hasInstant);

    if (shouldFetch) {
      debouncedFetch(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectPlace(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if the blur is moving to the dropdown menu
    const relatedTarget = e.relatedTarget as Node;
    if (menuRef.current && relatedTarget && menuRef.current.contains(relatedTarget)) {
      return; // Don't hide if focusing something in the menu
    }
    // Delay to allow click events to process
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (value && suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder}
          className={cn(
            'pr-8 truncate',
            error && 'border-destructive',
            className
          )}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-10">
          {isLoading && suggestions.length === 0 ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <MapPin className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && menuStyle && (
        createPortal(
          <div 
            ref={menuRef}
            style={menuStyle}
            role="listbox"
            className="bg-popover border border-border rounded-md shadow-lg max-h-[50vh] overflow-y-auto overscroll-contain pointer-events-auto touch-pan-y z-[10003]"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id}
                type="button"
                role="option"
                aria-selected={selectedIndex === index}
                onMouseDown={(e) => e.preventDefault()} // keep input focused to avoid premature blur
                className={cn(
                  'w-full px-3 py-2 text-left text-sm transition-colors focus:outline-none focus:bg-muted focus:text-foreground focus-visible:ring-1 focus-visible:ring-ring',
                  'hover:bg-muted hover:text-foreground',
                  'flex items-start gap-2',
                  selectedIndex === index && 'bg-brand-teal-subtle text-foreground'
                )}
                onClick={() => handleSelectPlace(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{suggestion.text}</div>
                  <div className="text-xs text-muted-foreground">
                    {(() => {
                      // Check if this is a postcode result
                      const isPostcode = suggestion.id?.startsWith('postcode');
                      
                      if (isPostcode && suggestion.context) {
                        // For postcode results, show: City, State
                        const city = suggestion.context.find(ctx => ctx.id.startsWith('place'))?.text;
                        const state = suggestion.context.find(ctx => ctx.id.startsWith('region'))?.text;
                        const parts = [city, state].filter(Boolean);
                        return parts.join(', ');
                      }
                      
                      // For place/address results, show: State, Zip
                      const state = suggestion.context?.find(ctx => ctx.id.startsWith('region'))?.text;
                      const zip = suggestion.context?.find(ctx => ctx.id.startsWith('postcode'))?.text;
                      const parts = [state, zip].filter(Boolean);
                      
                      return parts.length > 0 ? parts.join(', ') : suggestion.place_name.split(',').slice(1).join(',').trim();
                    })()}
                  </div>
                </div>
              </button>
            ))}
          </div>,
          document.body
        )
      )}


      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};